import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  useTrashedDoctors,
  useRestoreDoctors,
  usePermanentlyDeleteDoctors,
  useForceDeleteDoctors,
} from '../../services/httpServices/queries';
import { useDebounce } from '../../hooks/useDebounce';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { AlertDialog } from '../../components/ui/AlertDialog';
import {
  Loader2,
  AlertCircle,
  Search,
  X,
  RotateCcw,
  Trash2,
  ArrowLeft,
  Stethoscope,
  Zap,
} from 'lucide-react';

const LIMIT = 20;

function getInitials(name?: string) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  const f = parts[0]?.charAt(0) || '';
  const l = parts[1]?.charAt(0) || '';
  return (f + l).toUpperCase() || '?';
}

export function DoctorsTrash() {
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [confirmTarget, setConfirmTarget] = useState<{ mode: 'single' | 'bulk'; doctor?: any } | null>(null);
  const [forceTarget, setForceTarget] = useState<{ mode: 'single' | 'bulk'; doctor?: any } | null>(null);
  const [blockedDialog, setBlockedDialog] = useState<{ title: string; messages: string[] } | null>(null);
  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, error } = useTrashedDoctors(page, LIMIT, {
    search: debouncedSearch.trim() || undefined,
  });
  const restoreDoctors = useRestoreDoctors();
  const permanentDelete = usePermanentlyDeleteDoctors();
  const forceDelete = useForceDeleteDoctors();

  const doctors = data?.data || [];
  const meta = data?.meta;

  useEffect(() => {
    setPage(1);
    setSelectedIds([]);
  }, [debouncedSearch]);

  const allSelected = doctors.length > 0 && doctors.every((d: any) => selectedIds.includes(d.id));
  const someSelected = selectedIds.length > 0;

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !doctors.some((d: any) => d.id === id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...doctors.map((d: any) => d.id)])));
    }
  };

  const toggleSelectOne = (id: string) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleRestore = async (id: string) => {
    try {
      const res: any = await restoreDoctors.mutateAsync([id]);
      const blocked = res?.data?.blocked ?? [];
      if (blocked.length > 0) {
        toast.error(blocked[0]?.reason || 'This doctor could not be restored');
        return;
      }
      setSelectedIds((prev) => prev.filter((x) => x !== id));
      toast.success('Doctor restored');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to restore doctor');
    }
  };

  const handleBulkRestore = async () => {
    try {
      const res: any = await restoreDoctors.mutateAsync(selectedIds);
      const restored = res?.data?.restored?.length ?? 0;
      const blocked = res?.data?.blocked ?? [];
      if (restored > 0) toast.success(`Restored ${restored} doctor${restored === 1 ? '' : 's'}`);
      if (blocked.length > 0) {
        toast.error(`${blocked.length} doctor(s) could not be restored: ${blocked[0]?.reason ?? ''}`);
      } else if (restored === 0) {
        toast.error('No doctors were restored');
      }
      setSelectedIds([]);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to restore doctors');
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmTarget) return;
    try {
      if (confirmTarget.mode === 'single') {
        const res: any = await permanentDelete.mutateAsync([confirmTarget.doctor.id]);
        const blocked = res?.data?.blocked ?? [];
        if (blocked.length > 0) {
          setBlockedDialog({
            title: 'Cannot delete permanently',
            messages: [
              `${confirmTarget.doctor.name || 'This doctor'} — ${blocked[0]?.reason ?? 'This doctor could not be permanently deleted.'}`,
            ],
          });
          return;
        }
        setSelectedIds((prev) => prev.filter((x) => x !== confirmTarget.doctor.id));
        toast.success('Doctor permanently deleted');
      } else {
        const res: any = await permanentDelete.mutateAsync(selectedIds);
        const deleted = res?.data?.deleted?.length ?? 0;
        const blocked = res?.data?.blocked ?? [];
        if (deleted > 0) toast.success(`Permanently deleted ${deleted} doctor${deleted === 1 ? '' : 's'}`);
        if (blocked.length > 0) {
          const nameById = new Map<string, string>(doctors.map((d: any) => [d.id, d.name || d.id]));
          setBlockedDialog({
            title: `Cannot permanently delete ${blocked.length} doctor${blocked.length === 1 ? '' : 's'}`,
            messages: blocked.map((b: any) => `${nameById.get(b.id) ?? b.id} — ${b.reason}`),
          });
        } else if (deleted === 0) {
          setBlockedDialog({
            title: 'No doctors were deleted',
            messages: ['None of the selected doctors could be permanently deleted.'],
          });
        }
        setSelectedIds([]);
      }
    } catch (err: any) {
      setBlockedDialog({
        title: 'Cannot delete permanently',
        messages: [err?.response?.data?.message || 'The doctor(s) could not be permanently deleted.'],
      });
    } finally {
      setConfirmTarget(null);
    }
  };

  const handleConfirmForceDelete = async () => {
    if (!forceTarget) return;
    const ids = forceTarget.mode === 'single' ? [forceTarget.doctor.id] : selectedIds;
    try {
      const res: any = await forceDelete.mutateAsync(ids);
      const deleted = res?.data?.deleted?.length ?? 0;
      const blocked = res?.data?.blocked ?? [];
      const purged: Record<string, number> = res?.data?.purged ?? {};
      const purgedTotal = Object.values(purged).reduce((sum, n) => sum + (n || 0), 0);

      if (deleted > 0) {
        toast.success(
          `Deleted ${deleted} doctor${deleted === 1 ? '' : 's'} and ${purgedTotal} linked record${purgedTotal === 1 ? '' : 's'}`,
        );
      }
      if (blocked.length > 0) {
        const nameById = new Map<string, string>(doctors.map((d: any) => [d.id, d.name || d.id]));
        setBlockedDialog({
          title: `Could not force-delete ${blocked.length} doctor${blocked.length === 1 ? '' : 's'}`,
          messages: blocked.map((b: any) => `${nameById.get(b.id) ?? b.id} — ${b.reason}`),
        });
      } else if (deleted === 0) {
        setBlockedDialog({
          title: 'No doctors were deleted',
          messages: ['None of the selected doctors could be force-deleted.'],
        });
      }
      setSelectedIds((prev) => prev.filter((x) => !ids.includes(x)));
    } catch (err: any) {
      setBlockedDialog({
        title: 'Cannot force delete',
        messages: [err?.response?.data?.message || 'The doctor(s) could not be force-deleted.'],
      });
    } finally {
      setForceTarget(null);
    }
  };

  const busy = restoreDoctors.isPending || permanentDelete.isPending || forceDelete.isPending;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/doctors')}
          className="p-2 rounded-lg hover:bg-background text-text-muted transition-colors cursor-pointer"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-danger/10 rounded-lg">
            <Trash2 className="w-5 h-5 text-danger" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-text">Doctors Trash</h1>
            <p className="text-xs text-text-muted">Restore doctors or delete them permanently</p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-surface rounded-xl border border-border p-4 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, doctor code, email, or phone..."
              className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
            />
          </div>
          {search && (
            <button
              onClick={() => setSearch('')}
              className="px-3 py-2 text-text-muted hover:text-danger border border-border hover:border-danger rounded-lg transition-colors text-sm flex items-center gap-1.5 whitespace-nowrap"
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Bulk actions */}
      {someSelected && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 shadow-sm flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-text">
            <span className="font-medium">{selectedIds.length}</span> selected
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleBulkRestore}
              disabled={busy}
              className="px-3 py-1.5 rounded-lg bg-success text-white text-sm font-medium hover:bg-success/90 transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              {restoreDoctors.isPending ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
              Restore
            </button>
            <button
              onClick={() => setConfirmTarget({ mode: 'bulk' })}
              disabled={busy}
              className="px-3 py-1.5 rounded-lg bg-danger text-white text-sm font-medium hover:bg-danger/90 transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              {permanentDelete.isPending ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              Delete permanently
            </button>
            <button
              onClick={() => setForceTarget({ mode: 'bulk' })}
              disabled={busy}
              className="px-3 py-1.5 rounded-lg border border-danger text-danger text-sm font-medium hover:bg-danger/10 transition-colors disabled:opacity-50 flex items-center gap-1.5"
              title="Delete even if linked to other records, removing them too"
            >
              {forceDelete.isPending ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
              Delete anyway
            </button>
            <button
              onClick={() => setSelectedIds([])}
              disabled={busy}
              className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <X size={14} />
              Clear selection
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to load trashed doctors</span>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-background/50">
                  <th className="px-4 py-3 w-10 text-left">
                    <input
                      ref={headerCheckboxRef}
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      disabled={doctors.length === 0}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
                      title="Select all"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Doctor</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Position</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Departments</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Trashed</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {doctors.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-16 text-center">
                      <Stethoscope className="w-10 h-10 mx-auto mb-3 text-text-muted/40" />
                      <p className="text-sm font-medium text-text-muted mb-1">
                        {search ? 'No trashed doctors match your search' : 'Trash is empty'}
                      </p>
                    </td>
                  </tr>
                ) : (
                  doctors.map((doctor: any) => {
                    const name = doctor.name || 'Unknown';
                    const doctorDepartments = doctor.departments && doctor.departments.length > 0
                      ? doctor.departments
                      : (doctor.department ? [doctor.department] : []);
                    return (
                      <tr key={doctor.id} className="hover:bg-background/50 transition-colors">
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(doctor.id)}
                            onChange={() => toggleSelectOne(doctor.id)}
                            className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary shrink-0">
                              {getInitials(name)}
                            </div>
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-text truncate" title={name}>{name}</div>
                              <span className="text-xs text-text-muted font-mono">{doctor.employeeCode || '—'}</span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-text">{doctor.designation?.name || '—'}</div>
                          {doctor.specializations?.length > 0 && (
                            <div className="text-xs text-text-muted">
                              {doctor.specializations.map((s: any) => s.label).join(', ')}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {doctorDepartments.length === 0 ? (
                            <span className="text-sm text-text-muted">—</span>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {doctorDepartments.map((dept: any, idx: number) => (
                                <span
                                  key={dept.id || `${dept.name}-${idx}`}
                                  className="px-2 py-0.5 rounded-md bg-background border border-border text-xs text-text-muted"
                                >
                                  {dept.name}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-text-muted">
                          {doctor.deletedAt ? new Date(doctor.deletedAt).toLocaleString() : '—'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => handleRestore(doctor.id)}
                              disabled={busy}
                              className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-success transition-colors disabled:opacity-50"
                              title="Restore"
                            >
                              <RotateCcw size={16} />
                            </button>
                            <button
                              onClick={() => setConfirmTarget({ mode: 'single', doctor })}
                              disabled={busy}
                              className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-danger transition-colors disabled:opacity-50"
                              title="Delete permanently"
                            >
                              <Trash2 size={16} />
                            </button>
                            <button
                              onClick={() => setForceTarget({ mode: 'single', doctor })}
                              disabled={busy}
                              className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-danger transition-colors disabled:opacity-50"
                              title="Delete anyway (removes all linked records)"
                            >
                              <Zap size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {meta && (
            <Pagination
              page={meta.page}
              totalPages={meta.totalPages}
              total={meta.total}
              limit={meta.limit}
              onPageChange={setPage}
              itemName="trashed doctors"
            />
          )}
        </div>
      )}

      <ConfirmDialog
        open={confirmTarget !== null}
        title={confirmTarget?.mode === 'bulk' ? 'Delete doctors permanently' : 'Delete doctor permanently'}
        description={
          confirmTarget?.mode === 'bulk' ? (
            `Permanently delete ${selectedIds.length} selected doctor(s)? This cannot be undone. Doctors still linked to other records cannot be deleted.`
          ) : (
            <>
              Permanently delete <strong>{confirmTarget?.doctor?.name}</strong>? This cannot be undone. Doctors still
              linked to other records cannot be deleted.
            </>
          )
        }
        confirmLabel="Delete permanently"
        loading={busy}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmTarget(null)}
      />

      <ConfirmDialog
        open={forceTarget !== null}
        title={forceTarget?.mode === 'bulk' ? 'Delete doctors anyway' : 'Delete doctor anyway'}
        description={
          forceTarget?.mode === 'bulk' ? (
            <>
              This permanently deletes <strong>{selectedIds.length} doctor(s) and every record linked to them</strong> —
              appointments, billing, revenue, attendance, prescriptions and the linked login account. This cannot be
              undone.
            </>
          ) : (
            <>
              This permanently deletes <strong>{forceTarget?.doctor?.name}</strong> and every record linked to them —
              appointments, billing, revenue, attendance, prescriptions and the linked login account. This cannot be
              undone.
            </>
          )
        }
        confirmLabel="Delete anyway"
        loading={forceDelete.isPending}
        onConfirm={handleConfirmForceDelete}
        onCancel={() => setForceTarget(null)}
      />

      <AlertDialog
        open={blockedDialog !== null}
        title={blockedDialog?.title ?? 'Cannot delete permanently'}
        messages={blockedDialog?.messages}
        onClose={() => setBlockedDialog(null)}
      />
    </div>
  );
}
